import type { CSSProperties, ReactNode, Ref, MouseEventHandler } from 'react';
import { useRef, useLayoutEffect, forwardRef, useState, useEffect, useCallback } from 'react';
import type { TargetAndTransition } from 'motion/react';
import { AnimatePresence, motion } from 'motion/react';

type FramerEasing = [number, number, number, number] | 'linear' | 'easeIn' | 'easeOut' | 'easeInOut';

/**
 * Convert CSS easing string to Framer Motion easing format.
 * Supports cubic-bezier() and common CSS easing keywords.
 */
const parseEasing = (easingString: string): FramerEasing => {
  const match = easingString.match(/cubic-bezier\(([^)]+)\)/);
  if (match) {
    const values = match[1].split(',').map((n) => parseFloat(n.trim()));
    if (values.length === 4 && values.every((v) => !Number.isNaN(v))) {
      return values as [number, number, number, number];
    }
  }
  // Map common CSS easing functions to Framer Motion equivalents
  if (easingString === 'linear') return 'linear';
  if (easingString === 'ease-in') return 'easeIn';
  if (easingString === 'ease-out') return 'easeOut';
  if (easingString === 'ease-in-out') return 'easeInOut';
  return 'linear';
};

/**
 * OdinTransition provides animated enter/exit transitions for components.
 *
 * @remarks
 * Children are wrapped in a `<div>` element for animation purposes.
 */
export interface OdinTransitionProps {
  start: boolean;
  timeout: number;
  showTransitionOptions?: string;
  hideTransitionOptions?: string;
  /**
   * When true this will not mount the components until the transition is activated.
   */
  mountOnEnter?: boolean;
  /**
   * When true this will unmount the component once the transition is closed.
   */
  unmountOnExit?: boolean;
  /**
   * Called when the enter transition starts.
   */
  onEntering?: (node: HTMLElement, isAppearing: boolean) => void;
  /**
   * Called when the enter transition completes.
   */
  onEntered?: (node: HTMLElement, isAppearing: boolean) => void;
  /**
   * Called synchronously when the exit transition starts.
   * Runs in useLayoutEffect before browser paint - keep callbacks lightweight.
   */
  onExit?: (node: HTMLElement) => void;
  /**
   * Called when the exit transition completes.
   */
  onExited?: (node: HTMLElement) => void;
  onMouseEnter?: MouseEventHandler<HTMLDivElement>;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

const OdinTransition = forwardRef<HTMLDivElement, OdinTransitionProps>(function OdinTransition(
  {
    start,
    timeout,
    showTransitionOptions = 'cubic-bezier(0, 0, 0.2, 1)',
    hideTransitionOptions = 'linear',
    mountOnEnter = true,
    unmountOnExit = true,
    onEntering,
    onEntered,
    onExit,
    onExited,
    onMouseEnter,
    className,
    style,
    children,
  }: OdinTransitionProps,
  ref: Ref<HTMLDivElement>
) {
  const durationInSeconds = timeout / 1000;

  const nodeRef = useRef<HTMLDivElement | null>(null);
  // Store the node reference before unmount so onExit and onExited can access it.
  // In unmountOnExit mode with AnimatePresence, the original element is unmounted
  // (setting nodeRef to null) while AnimatePresence animates a clone. This ref
  // preserves access to the node for our callbacks.
  const exitNodeRef = useRef<HTMLDivElement | null>(null);

  // Store latest callback refs to avoid stale closures without adding them to deps.
  // This follows the useEffectEvent pattern (React RFC) to ensure we always call
  // the current callback without requiring consumers to memoize.
  const onEnteringRef = useRef(onEntering);
  const onEnteredRef = useRef(onEntered);
  const onExitRef = useRef(onExit);
  const onExitedRef = useRef(onExited);

  useLayoutEffect(() => {
    onEnteringRef.current = onEntering;
    onEnteredRef.current = onEntered;
    onExitRef.current = onExit;
    onExitedRef.current = onExited;
  });

  // Combine internal nodeRef with forwarded ref.
  // We use a callback ref to forward the element to both our internal ref and the consumer's ref.
  // Also capture the node in exitNodeRef when it mounts, so we have a reference for onExit/onExited
  // even after AnimatePresence unmounts the original element.
  const setRef = useCallback(
    (element: HTMLDivElement | null) => {
      nodeRef.current = element;
      // Capture non-null reference for exit callbacks
      if (element !== null) {
        exitNodeRef.current = element;
      }
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        // TypeScript types RefObject.current as readonly, but React itself mutates it.
        // We use Object.assign to avoid the type cast while still being type-safe.
        Object.assign(ref, { current: element });
      }
    },
    [ref]
  );

  // Track whether this is the initial appearance (first mount)
  const isFirstAppearance = useRef(true);

  const prevStartRef = useRef(start);

  // Track current value of start for use in animation callbacks to avoid stale closures.
  // Framer Motion's onAnimationComplete captures the value of start at animation start time,
  // so if start changes during the animation, the callback would read the stale value.
  const startRef = useRef(start);
  useLayoutEffect(() => {
    startRef.current = start;
  }, [start]);

  // For unmountOnExit=false mode: track if component has ever been shown
  const [hasBeenMounted, setHasBeenMounted] = useState(!mountOnEnter || start);

  // When start becomes true for the first time, mark as mounted
  useEffect(() => {
    if (start && !hasBeenMounted) {
      setHasBeenMounted(true);
    }
  }, [start, hasBeenMounted]);

  // Trigger onExit synchronously when start changes from true to false.
  // useLayoutEffect fires before browser paint, matching react-transition-group's
  // synchronous callback timing. We use exitNodeRef instead of nodeRef because
  // in unmountOnExit mode with AnimatePresence, the original element is unmounted
  // (setting nodeRef to null) before this effect runs. exitNodeRef preserves the
  // reference captured when the element was mounted.
  useLayoutEffect(() => {
    if (prevStartRef.current && !start && onExitRef.current && exitNodeRef.current) {
      onExitRef.current(exitNodeRef.current);
    }
    prevStartRef.current = start;
  }, [start]);

  const handleAnimationStart = () => {
    // Only fire onEntering when animating TO visible state
    // Use startRef.current for consistency with handleAnimationComplete
    if (startRef.current && onEnteringRef.current && nodeRef.current) {
      onEnteringRef.current(nodeRef.current, isFirstAppearance.current);
    }
  };

  const handleAnimationComplete = () => {
    // Use startRef.current to read the current value of start, not the stale closure value.
    // Framer Motion's onAnimationComplete captures the value of start at animation start time,
    // so if start changed during the animation (e.g., user closed the overlay while it was
    // still animating in), the callback would incorrectly think the animation was an "enter".
    const currentStart = startRef.current;
    if (currentStart) {
      // Entering animation complete
      if (onEnteredRef.current && nodeRef.current) {
        onEnteredRef.current(nodeRef.current, isFirstAppearance.current);
      }
      // After the first animation completes, subsequent animations are not "appearing"
      isFirstAppearance.current = false;
    } else if (!unmountOnExit) {
      // Exit animation complete - only fire here for unmountOnExit=false mode.
      // In unmountOnExit=true mode, onExited is fired by handleExitComplete instead,
      // which uses the captured exitNodeRef since the element will be unmounted.
      if (onExitedRef.current && nodeRef.current) {
        onExitedRef.current(nodeRef.current);
      }
    }
  };

  const handleExitComplete = () => {
    // Use the captured node reference from before unmount
    if (onExitedRef.current && exitNodeRef.current) {
      onExitedRef.current(exitNodeRef.current);
      exitNodeRef.current = null;
    }
  };

  // When mountOnEnter is true, start from opacity 0 and animate to visible.
  // When mountOnEnter is false, initial={false} tells Framer Motion to skip the enter animation
  // and render the component directly in its animated (visible) state.
  // This prevents existing DOM nodes (when mountOnEnter=false) from briefly flashing at opacity 0.
  const initialState: boolean | TargetAndTransition = mountOnEnter ? { opacity: 0 } : false;

  const visibleState: TargetAndTransition = {
    opacity: 1,
    pointerEvents: 'auto',
  };

  const hiddenState: TargetAndTransition = {
    opacity: 0,
    pointerEvents: 'none',
  };

  // When unmountOnExit is false, we always render but animate between visible/hidden
  if (!unmountOnExit) {
    // Don't render until first shown (respects mountOnEnter)
    if (!hasBeenMounted) {
      return null;
    }

    return (
      <motion.div
        ref={setRef}
        className={className}
        style={style}
        onMouseEnter={onMouseEnter}
        initial={initialState}
        animate={start ? visibleState : hiddenState}
        transition={{
          duration: durationInSeconds,
          ease: parseEasing(start ? showTransitionOptions : hideTransitionOptions),
        }}
        onAnimationStart={handleAnimationStart}
        onAnimationComplete={handleAnimationComplete}
      >
        {children}
      </motion.div>
    );
  }

  // When unmountOnExit is true (default), use AnimatePresence for proper exit animations
  // The transition prop on motion.div applies to animate (enter), while exit.transition
  // provides a separate configuration for exit animations (different easing)
  return (
    <AnimatePresence mode="wait" onExitComplete={handleExitComplete}>
      {start && (
        <motion.div
          ref={setRef}
          className={className}
          style={style}
          onMouseEnter={onMouseEnter}
          initial={initialState}
          animate={visibleState}
          exit={{
            ...hiddenState,
            transition: {
              duration: durationInSeconds,
              ease: parseEasing(hideTransitionOptions),
            },
          }}
          transition={{
            duration: durationInSeconds,
            ease: parseEasing(showTransitionOptions),
          }}
          onAnimationStart={handleAnimationStart}
          onAnimationComplete={handleAnimationComplete}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default OdinTransition;
